import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  COACHES_QUERY_KEY,
  useCoachContent,
  useDeleteCoach,
  useRestoreCoach,
  useSuspendCoach,
  useUpdateCoach,
} from "@/hooks/useCoaches";
import {
  coachService,
  type CoachContentType,
} from "@/lib/api/coachService";
import { useAuth, usePermission } from "@/lib/auth";
import { safeHref, socialLinksToArray } from "@/lib/coach-social-links";
import { queryClient } from "@/lib/queryClient";
import type {
  Coach,
  CoachSocialLinks,
  CoachType,
  SocialLink,
} from "@/lib/schema";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  Calendar,
  Camera,
  CheckCircle,
  Edit3,
  FileText,
  Globe,
  Instagram,
  Linkedin,
  Link as LinkIcon,
  Loader2,
  MapPin,
  MoreHorizontal,
  Save,
  Shield,
  ShieldOff,
  ShoppingBag,
  Star,
  Trash2,
  User,
  Users,
  Youtube,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SiTiktok, SiX } from "react-icons/si";
import { useLocation, useRoute } from "wouter";

const expertiseOptions = [
  "Meditation",
  "Breathwork",
  "Movement",
  "Mindfulness",
  "Journaling",
  "Affirmations",
  "Yoga",
  "Sleep",
  "Stress Relief",
  "Dance Therapy",
  "Body Awareness",
  "Clinical Psychology",
  "Self-Discovery",
  "Creative Writing",
];

const languageOptions = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "pt", name: "Portuguese" },
  { code: "de", name: "German" },
  { code: "fr", name: "French" },
  { code: "zh", name: "Chinese" },
  { code: "ko", name: "Korean" },
];

const coachTypeLabels: Record<CoachType, string> = {
  verified_coach: "Verified",
  coach: "Coach",
  guest: "Guest",
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const PLATFORM_TO_SOCIAL_KEY: Record<
  SocialLink["platform"],
  keyof CoachSocialLinks
> = {
  Instagram: "instagram",
  LinkedIn: "linkedin",
  Twitter: "twitter",
  YouTube: "youtube",
  TikTok: "tiktok",
  Website: "website",
  Store: "store",
  BookingCalendar: "bookingCalendar",
  RetreatPage: "retreatPage",
};

function arrayToSocialLinks(
  links: Coach["socialLinks"] | null | undefined,
): CoachSocialLinks {
  const out: CoachSocialLinks = {};
  if (!Array.isArray(links)) return out;
  for (const link of links) {
    const key = PLATFORM_TO_SOCIAL_KEY[link.platform];
    if (key && link.url) out[key] = link.url;
  }
  return out;
}

const CONTENT_TYPE_LABELS: Record<CoachContentType, string> = {
  audio: "Audio",
  video: "Video",
  blog: "Blog",
  recipe: "Recipe",
  affirmation: "Affirmation",
  challenge: "Challenge",
  journey: "Journey",
};

const CONTENT_TYPE_ORDER: CoachContentType[] = [
  "audio",
  "video",
  "blog",
  "recipe",
  "affirmation",
  "challenge",
  "journey",
];

interface CoachProfileFormData {
  bio: string;
  photoUrl: string;
  expertise: string[];
  languages: string[];
  city: string;
  state: string;
  country: string;
  coachType: CoachType;
  socialLinks: CoachSocialLinks;
}

export default function CoachProfile() {
  const [, params] = useRoute("/coaches/:id/profile");
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<CoachProfileFormData | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    type: "suspend" | "restore" | "delete" | null;
  }>({ type: null });
  const [actionReason, setActionReason] = useState("");
  const [contentTypeFilter, setContentTypeFilter] = useState<
    CoachContentType | "all"
  >("all");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const coachId = params?.id;
  const userRole = user?.roles;

  // Permission checks
  const canSuspend = usePermission("coaches", "suspend");
  const canRestore = usePermission("coaches", "restore");
  const canDelete = usePermission("coaches", "delete");
  const canViewAudit = usePermission("coaches", "view_audit_history");

  const isAdmin =
    userRole?.includes("super_admin") ||
    userRole?.includes("admin") ||
    userRole?.includes("coach_manager");
  const canEditCoachType =
    userRole?.includes("super_admin") || userRole?.includes("admin");

  const { data: coach, isLoading } = useQuery<Coach | null>({
    queryKey: [...COACHES_QUERY_KEY, coachId],
    queryFn: () => coachService.getCoachById(coachId as string),
    enabled: !!coachId,
  });

  const { data: coachContent, isLoading: coachContentLoading } =
    useCoachContent(coachId);

  const isOwnProfile =
    userRole?.includes("coach") && coach?.userId === user?.id;
  const canView = isAdmin || isOwnProfile;
  const canEditProfileFields = isOwnProfile || isAdmin;

  useEffect(() => {
    if (coach) {
      const socialLinks: CoachSocialLinks = arrayToSocialLinks(
        coach.socialLinks,
      );
      setFormData({
        bio: coach.bio || "",
        photoUrl: coach.photoUrl || "",
        expertise: coach.expertise || [],
        languages: coach.languages || [],
        city: coach.city || "",
        state: coach.state || "",
        country: coach.country || "",
        coachType: coach.coachType as CoachType,
        socialLinks,
      });
    }
  }, [coach]);

  const updateMutation = useUpdateCoach();
  const suspendMutation = useSuspendCoach();
  const restoreMutation = useRestoreCoach();
  const deleteMutation = useDeleteCoach();

  const handleSave = () => {
    if (!formData || !coach) return;

    const updateData: Partial<Coach> = {};

    if (canEditProfileFields) {
      updateData.bio = formData.bio;
      updateData.expertise = formData.expertise;
      updateData.languages = formData.languages;
      updateData.city = formData.city;
      updateData.state = formData.state;
      updateData.country = formData.country;
      updateData.socialLinks = socialLinksToArray(formData.socialLinks);
    }

    if (canEditCoachType) {
      updateData.coachType = formData.coachType;
    }

    if (!coachId) return;
    updateMutation.mutate(
      { id: coachId, data: updateData },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      },
    );
  };

  const handleCancel = () => {
    if (coach) {
      const socialLinks: CoachSocialLinks = arrayToSocialLinks(
        coach.socialLinks,
      );
      setFormData({
        bio: coach.bio || "",
        photoUrl: coach.photoUrl || "",
        expertise: coach.expertise || [],
        languages: coach.languages || [],
        city: coach.city || "",
        state: coach.state || "",
        country: coach.country || "",
        coachType: coach.coachType as CoachType,
        socialLinks,
      });
    }
    setIsEditing(false);
  };

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !coachId) return;

    setIsUploadingPhoto(true);
    try {
      const updated = await coachService.updateCoach(coachId, {}, file);
      if (formData) {
        setFormData({ ...formData, photoUrl: updated.photoUrl || "" });
      }
      queryClient.invalidateQueries({
        queryKey: [...COACHES_QUERY_KEY, coachId],
      });
      queryClient.invalidateQueries({ queryKey: COACHES_QUERY_KEY });
      toast({ title: "Photo uploaded successfully" });
    } catch (error) {
      toast({ title: "Failed to upload photo", variant: "destructive" });
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const toggleExpertise = (expertise: string) => {
    if (!formData) return;
    const current = formData.expertise || [];
    if (current.includes(expertise)) {
      setFormData({
        ...formData,
        expertise: current.filter((e) => e !== expertise),
      });
    } else {
      setFormData({ ...formData, expertise: [...current, expertise] });
    }
  };

  const toggleLanguage = (code: string) => {
    if (!formData) return;
    const current = formData.languages || [];
    if (current.includes(code)) {
      setFormData({
        ...formData,
        languages: current.filter((l) => l !== code),
      });
    } else {
      setFormData({ ...formData, languages: [...current, code] });
    }
  };

  const updateSocialLink = (
    platform: keyof CoachSocialLinks,
    value: string,
  ) => {
    if (!formData) return;
    setFormData({
      ...formData,
      socialLinks: { ...formData.socialLinks, [platform]: value },
    });
  };

  const handleAction = (type: "suspend" | "restore" | "delete") => {
    setActionDialog({ type });
  };

  const executeAction = () => {
    if (!coachId) return;
    switch (actionDialog.type) {
      case "suspend":
        if (!actionReason.trim()) {
          toast({ title: "Reason is required", variant: "destructive" });
          return;
        }
        suspendMutation.mutate(
          { id: coachId, reason: actionReason },
          {
            onSuccess: () => {
              setActionDialog({ type: null });
              setActionReason("");
            },
          },
        );
        break;
      case "restore":
        restoreMutation.mutate(coachId, {
          onSuccess: () => setActionDialog({ type: null }),
        });
        break;
      case "delete":
        if (!actionReason.trim()) {
          toast({ title: "Reason is required", variant: "destructive" });
          return;
        }
        deleteMutation.mutate(
          { id: coachId, reason: actionReason },
          {
            onSuccess: () => {
              setActionDialog({ type: null });
              setActionReason("");
              setLocation("/coaches");
            },
          },
        );
        break;
    }
  };

  if (!coachId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Coach not found</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Coach not found</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          You don't have permission to view this profile
        </p>
      </div>
    );
  }

  const socialLinks: CoachSocialLinks = arrayToSocialLinks(coach.socialLinks);

  const getLanguageName = (code: string) => {
    const lang = languageOptions.find((l) => l.code === code);
    return lang ? lang.name : code.toUpperCase();
  };

  const isSuspended = coach.status === "suspended";

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLocation("/coaches")}
        data-testid="button-back-to-coaches"
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Coaches
      </Button>

      {/* Profile Header Card */}
      <Card
        className="rounded-xl overflow-hidden"
        data-testid="card-profile-header"
      >
        <CardContent className="p-0">
          <div className="evo-gradient-subtle p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              {/* Avatar with upload */}
              <div className="relative group">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="input-photo-upload"
                />
                <div className="h-24 w-24 lg:h-28 lg:w-28 rounded-2xl bg-card flex items-center justify-center overflow-hidden shadow-sm">
                  <Avatar
                    className={`h-full w-full rounded-2xl ${isEditing ? "cursor-pointer" : ""}`}
                  >
                    <AvatarImage
                      src={formData?.photoUrl || coach.photoUrl || undefined}
                      alt={coach.name}
                      className="object-cover"
                    />
                    <AvatarFallback className="rounded-2xl bg-primary/10 text-primary text-2xl lg:text-3xl font-semibold">
                      {getInitials(coach.name)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                {isEditing && (
                  <div
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isUploadingPhoto ? (
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    ) : (
                      <Camera className="h-6 w-6 text-white" />
                    )}
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <h1
                      className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight"
                      data-testid="text-coach-name"
                    >
                      {coach.name}
                    </h1>
                    <p
                      className="text-muted-foreground text-sm mt-1"
                      data-testid="text-coach-email"
                    >
                      {coach.email}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <Badge variant="secondary" data-testid="badge-coach-type">
                        {coachTypeLabels[coach.coachType as CoachType]}
                      </Badge>
                      <Badge
                        variant={
                          coach.status === "active" ? "default" : "outline"
                        }
                        className={
                          coach.status === "active"
                            ? "border-primary/30 bg-primary/5 text-primary"
                            : coach.status === "suspended"
                              ? "border-destructive/30 bg-destructive/5 text-destructive"
                              : ""
                        }
                        data-testid="badge-coach-status"
                      >
                        {coach.status.charAt(0).toUpperCase() +
                          coach.status.slice(1)}
                      </Badge>
                      {coach.coachType === "verified_coach" && (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  </div>

                  {/* Actions Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="button-coach-actions"
                      >
                        Actions <MoreHorizontal className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {canEditProfileFields && !isEditing && (
                        <DropdownMenuItem
                          onClick={() => setIsEditing(true)}
                          data-testid="menu-item-edit"
                        >
                          <Edit3 className="mr-2 h-4 w-4" />
                          Edit Profile
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      {canSuspend && !isSuspended && (
                        <DropdownMenuItem
                          onClick={() => handleAction("suspend")}
                          className="text-destructive focus:text-destructive"
                          data-testid="menu-item-suspend"
                        >
                          <Shield className="mr-2 h-4 w-4" />
                          Suspend Coach
                        </DropdownMenuItem>
                      )}
                      {canRestore && isSuspended && (
                        <DropdownMenuItem
                          onClick={() => handleAction("restore")}
                          data-testid="menu-item-restore"
                        >
                          <ShieldOff className="mr-2 h-4 w-4" />
                          Restore Coach
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          onClick={() => handleAction("delete")}
                          className="text-destructive focus:text-destructive"
                          data-testid="menu-item-delete"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Coach
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suspended Alert */}
      {isSuspended && (
        <Card className="border-destructive/50 bg-destructive/5 rounded-xl">
          <CardContent className="flex items-center gap-4 py-5 px-6">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-medium text-destructive">Coach Suspended</p>
              <p className="text-sm text-muted-foreground">
                This coach's account is currently suspended
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Mode Banner */}
      {isEditing && (
        <Card className="border-primary/50 bg-primary/5 rounded-xl">
          <CardContent className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-3">
              <Edit3 className="h-5 w-5 text-primary" />
              <p className="font-medium text-primary">Editing Profile</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
                data-testid="button-cancel-edit"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={updateMutation.isPending}
                data-testid="button-save-profile"
              >
                <Save className="h-4 w-4 mr-2" />
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Row */}
      <div
        className="grid gap-4 grid-cols-2"
        data-testid="section-quick-stats"
      >
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p
                  className="text-2xl font-semibold"
                  data-testid="stat-content-count"
                >
                  {coach.contentCount ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Content</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center">
                <Award className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p
                  className="text-2xl font-semibold"
                  data-testid="stat-expertise-count"
                >
                  {coach.expertise?.length || 0}
                </p>
                <p className="text-xs text-muted-foreground">Specialties</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Coach Details Card */}
        <Card className="rounded-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <CardTitle className="text-lg">Coach Details</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Name
                </p>
                <p className="font-medium">{coach.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Email
                </p>
                <p className="font-medium">{coach.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  City
                </p>
                {isEditing ? (
                  <Input
                    placeholder="City"
                    value={formData?.city || ""}
                    onChange={(e) =>
                      setFormData(
                        formData
                          ? { ...formData, city: e.target.value }
                          : null,
                      )
                    }
                    className="h-8"
                    data-testid="input-city"
                  />
                ) : (
                  <p className="font-medium">{coach.city || "Not set"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  State
                </p>
                {isEditing ? (
                  <Input
                    placeholder="State / Province"
                    value={formData?.state || ""}
                    onChange={(e) =>
                      setFormData(
                        formData
                          ? { ...formData, state: e.target.value }
                          : null,
                      )
                    }
                    className="h-8"
                    data-testid="input-state"
                  />
                ) : (
                  <p className="font-medium">{coach.state || "Not set"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Country
                </p>
                {isEditing ? (
                  <Input
                    placeholder="Country"
                    value={formData?.country || ""}
                    onChange={(e) =>
                      setFormData(
                        formData
                          ? { ...formData, country: e.target.value }
                          : null,
                      )
                    }
                    className="h-8"
                    data-testid="input-country"
                  />
                ) : (
                  <p className="font-medium">{coach.country || "Not set"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Languages
                </p>
                {isEditing ? (
                  <div className="flex flex-wrap gap-1">
                    {languageOptions.map((lang) => (
                      <Badge
                        key={lang.code}
                        variant={
                          formData?.languages?.includes(lang.code)
                            ? "default"
                            : "outline"
                        }
                        className="cursor-pointer text-xs"
                        onClick={() => toggleLanguage(lang.code)}
                        data-testid={`badge-lang-${lang.code}`}
                      >
                        {lang.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium">
                    {coach.languages?.length
                      ? coach.languages.map(getLanguageName).join(", ")
                      : "Not set"}
                  </p>
                )}
              </div>
              {canEditCoachType && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Coach Type
                  </p>
                  {isEditing ? (
                    <Select
                      value={formData?.coachType || "coach"}
                      onValueChange={(value) =>
                        setFormData(
                          formData
                            ? { ...formData, coachType: value as CoachType }
                            : null,
                        )
                      }
                    >
                      <SelectTrigger
                        className="h-8 px-3 py-1 text-sm"
                        data-testid="select-coach-type"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="verified_coach">Verified</SelectItem>
                        <SelectItem value="coach">Coach</SelectItem>
                        <SelectItem value="guest">Guest</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">
                      {coachTypeLabels[coach.coachType as CoachType]}
                    </p>
                  )}
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Joined
                </p>
                <p className="font-medium">
                  {coach.invitedAt
                    ? new Date(coach.invitedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Last Active
                </p>
                <p className="font-medium">
                  {coach.lastActiveAt
                    ? new Date(coach.lastActiveAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "Never"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Links Card */}
        <Card className="rounded-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <LinkIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <CardTitle className="text-lg">Social Links</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {isEditing ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Instagram className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="Instagram URL"
                    value={formData?.socialLinks?.instagram || ""}
                    onChange={(e) =>
                      updateSocialLink("instagram", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-instagram"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="LinkedIn URL"
                    value={formData?.socialLinks?.linkedin || ""}
                    onChange={(e) =>
                      updateSocialLink("linkedin", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-linkedin"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <SiX className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="X (Twitter) URL"
                    value={formData?.socialLinks?.twitter || ""}
                    onChange={(e) =>
                      updateSocialLink("twitter", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-twitter"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Youtube className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="YouTube URL"
                    value={formData?.socialLinks?.youtube || ""}
                    onChange={(e) =>
                      updateSocialLink("youtube", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-youtube"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <SiTiktok className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="TikTok URL"
                    value={formData?.socialLinks?.tiktok || ""}
                    onChange={(e) => updateSocialLink("tiktok", e.target.value)}
                    className="h-8"
                    data-testid="input-social-tiktok"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="Store / Shop URL"
                    value={formData?.socialLinks?.store || ""}
                    onChange={(e) => updateSocialLink("store", e.target.value)}
                    className="h-8"
                    data-testid="input-social-store"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="Booking Calendar URL"
                    value={formData?.socialLinks?.bookingCalendar || ""}
                    onChange={(e) =>
                      updateSocialLink("bookingCalendar", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-booking-calendar"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="Retreat Page URL"
                    value={formData?.socialLinks?.retreatPage || ""}
                    onChange={(e) =>
                      updateSocialLink("retreatPage", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-retreat-page"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="Website URL"
                    value={formData?.socialLinks?.website || ""}
                    onChange={(e) =>
                      updateSocialLink("website", e.target.value)
                    }
                    className="h-8"
                    data-testid="input-social-website"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {socialLinks.instagram && (
                  <a
                    href={safeHref(socialLinks.instagram)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    <span className="truncate">Instagram</span>
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={safeHref(socialLinks.linkedin)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Linkedin className="h-4 w-4" />
                    <span className="truncate">LinkedIn</span>
                  </a>
                )}
                {socialLinks.twitter && (
                  <a
                    href={safeHref(socialLinks.twitter)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <SiX className="h-4 w-4" />
                    <span className="truncate">X (Twitter)</span>
                  </a>
                )}
                {socialLinks.youtube && (
                  <a
                    href={safeHref(socialLinks.youtube)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Youtube className="h-4 w-4" />
                    <span className="truncate">YouTube</span>
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a
                    href={safeHref(socialLinks.tiktok)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <SiTiktok className="h-4 w-4" />
                    <span className="truncate">TikTok</span>
                  </a>
                )}
                {socialLinks.store && (
                  <a
                    href={safeHref(socialLinks.store)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ShoppingBag className="h-4 w-4" />
                    <span className="truncate">Store</span>
                  </a>
                )}
                {socialLinks.bookingCalendar && (
                  <a
                    href={safeHref(socialLinks.bookingCalendar)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Calendar className="h-4 w-4" />
                    <span className="truncate">Booking Calendar</span>
                  </a>
                )}
                {socialLinks.retreatPage && (
                  <a
                    href={safeHref(socialLinks.retreatPage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="truncate">Retreat Page</span>
                  </a>
                )}
                {socialLinks.website && (
                  <a
                    href={safeHref(socialLinks.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="truncate">Website</span>
                  </a>
                )}
                {!Object.values(socialLinks).some(Boolean) && (
                  <p className="text-sm text-muted-foreground">
                    No social links added
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bio Section */}
      <Card className="rounded-xl">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg">Bio</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Textarea
              placeholder="Write a brief bio..."
              className="min-h-[100px] resize-none"
              value={formData?.bio || ""}
              onChange={(e) =>
                setFormData(
                  formData ? { ...formData, bio: e.target.value } : null,
                )
              }
              data-testid="input-bio"
            />
          ) : (
            <p className="text-sm text-foreground leading-relaxed">
              {coach.bio || "No bio provided"}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Expertise Section */}
      <Card className="rounded-xl">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
              <Award className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg">Expertise</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="flex flex-wrap gap-2">
              {expertiseOptions.map((exp) => (
                <Badge
                  key={exp}
                  variant={
                    formData?.expertise?.includes(exp) ? "default" : "outline"
                  }
                  className="cursor-pointer"
                  onClick={() => toggleExpertise(exp)}
                  data-testid={`badge-expertise-${exp.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {exp}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {coach.expertise?.length ? (
                coach.expertise.map((exp) => (
                  <Badge key={exp} variant="secondary">
                    {exp}
                  </Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No expertise listed
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Additional Content */}
      <Tabs defaultValue="content" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="content" data-testid="tab-content">
            Content
          </TabsTrigger>
          {canViewAudit && (
            <TabsTrigger value="audit" data-testid="tab-audit">
              Audit History
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="content" className="mt-4">
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base">Published Content</CardTitle>
              <CardDescription>
                Content authored by this coach and currently published
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filter chips */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={contentTypeFilter === "all" ? "default" : "outline"}
                  onClick={() => setContentTypeFilter("all")}
                  data-testid="filter-content-all"
                >
                  All ({coachContent?.total ?? 0})
                </Button>
                {CONTENT_TYPE_ORDER.map((type) => {
                  const count =
                    coachContent?.items?.filter(
                      (i) => i.contentType === type,
                    ).length ?? 0;
                  if (count === 0) return null;
                  return (
                    <Button
                      key={type}
                      size="sm"
                      variant={
                        contentTypeFilter === type ? "default" : "outline"
                      }
                      onClick={() => setContentTypeFilter(type)}
                      data-testid={`filter-content-${type}`}
                    >
                      {CONTENT_TYPE_LABELS[type]} ({count})
                    </Button>
                  );
                })}
              </div>

              {/* Content grid */}
              {coachContentLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">Loading…</p>
                </div>
              ) : (() => {
                  const filtered =
                    contentTypeFilter === "all"
                      ? coachContent?.items ?? []
                      : (coachContent?.items ?? []).filter(
                          (i) => i.contentType === contentTypeFilter,
                        );
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                          {contentTypeFilter === "all"
                            ? "No published content yet"
                            : `No ${CONTENT_TYPE_LABELS[contentTypeFilter]} content yet`}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.map((item) => (
                        <div
                          key={`${item.contentType}-${item.id}`}
                          className="flex gap-3 p-3 rounded-lg border bg-card"
                          data-testid={`content-card-${item.id}`}
                        >
                          <div className="w-16 h-16 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                            {item.thumbnailUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={item.thumbnailUrl}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <FileText className="h-6 w-6 text-muted-foreground/50" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">
                              {item.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs capitalize">
                                {CONTENT_TYPE_LABELS[item.contentType]}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                Published
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
            </CardContent>
          </Card>
        </TabsContent>

        {canViewAudit && (
          <TabsContent value="audit" className="mt-4">
            <Card className="rounded-xl">
              <CardHeader>
                <CardTitle className="text-base">Audit History</CardTitle>
                <CardDescription>
                  Record of profile changes and actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No audit entries yet</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Action Dialogs */}
      <Dialog
        open={actionDialog.type === "suspend"}
        onOpenChange={(open) => !open && setActionDialog({ type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Coach</DialogTitle>
            <DialogDescription>
              This will suspend the coach's account. They will not be able to
              access the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">
                Reason for suspension
              </label>
              <Textarea
                placeholder="Enter the reason..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="mt-2"
                data-testid="input-suspend-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ type: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeAction}
              disabled={suspendMutation.isPending}
              data-testid="button-confirm-suspend"
            >
              {suspendMutation.isPending ? "Suspending..." : "Suspend Coach"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={actionDialog.type === "restore"}
        onOpenChange={(open) => !open && setActionDialog({ type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Coach</DialogTitle>
            <DialogDescription>
              This will restore the coach's account and they will be able to
              access the platform again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ type: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={restoreMutation.isPending}
              data-testid="button-confirm-restore"
            >
              {restoreMutation.isPending ? "Restoring..." : "Restore Coach"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={actionDialog.type === "delete"}
        onOpenChange={(open) => !open && setActionDialog({ type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Coach</DialogTitle>
            <DialogDescription>
              This action is irreversible. The coach and all associated data
              will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Reason for deletion</label>
              <Textarea
                placeholder="Enter the reason..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="mt-2"
                data-testid="input-delete-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ type: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeAction}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Coach"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
